import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
    Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {useTranslation} from 'react-i18next';
import NewContractFormComponent, {isContractFormValid} from '../../Generic/NewContractFormComponent';
import type {Contract} from '../../../types/contract';
import type {Employee} from '../../../types/employee';

interface ApprovedBranch {
    id: string;
    name: string;
}

interface AddContractDialogProps {
    open: boolean;
    branches: ApprovedBranch[];
    branchId: string;
    employees: Employee[];
    allEmployeesHaveContract?: boolean;
    employeeId: string;
    contract: Contract;
    submitting?: boolean;
    onClose: () => void;
    onSubmit: () => void;
    onBranchChange: (event: SelectChangeEvent<string>) => void;
    onEmployeeChange: (event: SelectChangeEvent<string>) => void;
    handleContractFormChange: (field: keyof Contract) => (event: any) => void;
    contractSubmitError?: { message: string; violations: string[] } | null;
}

const AddContractDialog: React.FC<AddContractDialogProps> = ({
                                                                   open,
                                                                   branches,
                                                                   branchId,
                                                                   employees,
                                                                   allEmployeesHaveContract = false,
                                                                   employeeId,
                                                                   contract,
                                                                   submitting = false,
                                                                   onClose,
                                                                   onSubmit,
                                                                   onBranchChange,
                                                                   onEmployeeChange,
                                                                   handleContractFormChange,
                                                                   contractSubmitError,
                                                               }) => {
    const {t} = useTranslation();
    const [showAllErrors, setShowAllErrors] = React.useState(false);

    const branchName = branches.find((b) => b.id === branchId)?.name ?? '';

    const handleSave = () => {
        if (!employeeId || !isContractFormValid(contract)) {
            setShowAllErrors(true);
            return;
        }
        setShowAllErrors(false);
        onSubmit();
    };

    const handleClose = () => {
        setShowAllErrors(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={() => {
        }} disableEscapeKeyDown maxWidth={false} PaperProps={{sx: {width: {xs: 'calc(100% - 32px)', sm: '600px'}, margin: 'auto'}}}>
            <DialogTitle>{t('contracts.dialog.addTitle')}</DialogTitle>
            <DialogContent>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 2}}>
                    <Typography variant="subtitle2" color="primary" sx={{mb: 0}}>
                        {t('contracts.add.selectEmployeeSection')}
                    </Typography>
                    <FormControl fullWidth required>
                        <InputLabel id="add-contract-branch-label">{t('common.filters.selectBranch')}</InputLabel>
                        <Select
                            labelId="add-contract-branch-label"
                            value={branchId}
                            label={t('common.filters.selectBranch')}
                            onChange={onBranchChange}
                        >
                            {branches.map((branch) => (
                                <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth required error={showAllErrors && !employeeId}>
                        <InputLabel id="add-contract-employee-label">{t('contracts.add.selectEmployee')}</InputLabel>
                        <Select
                            labelId="add-contract-employee-label"
                            value={employeeId}
                            label={t('contracts.add.selectEmployee')}
                            onChange={onEmployeeChange}
                            disabled={!branchId || employees.length === 0}
                        >
                            {employees.length === 0 ? (
                                <MenuItem disabled value="">
                                    {allEmployeesHaveContract
                                        ? t('contracts.add.allEmployeesHaveContract')
                                        : t('contracts.add.noEmployeesInBranch')}
                                </MenuItem>
                            ) : (
                                employees.map((employee) => (
                                    <MenuItem key={employee.id} value={String(employee.id)}>
                                        {employee.firstName} {employee.lastName}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                        {showAllErrors && !employeeId && (
                            <FormHelperText>{t('contracts.add.selectEmployeeError')}</FormHelperText>
                        )}
                    </FormControl>

                    <Divider sx={{my: 1}}/>

                    <NewContractFormComponent
                        newContract={contract}
                        branchName={branchName}
                        handleContractFormChange={handleContractFormChange}
                        showAllErrors={showAllErrors}
                        backendError={contractSubmitError}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="inherit" disabled={submitting}>
                    {t('common.actions.cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit"/> : <SaveIcon/>}
                >
                    {t('common.actions.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddContractDialog;
